import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import { CompassCalibration } from "@mui/icons-material";
import { AddEvent } from "./AddEvent";
import { ContentWrapper } from "../../components/ContentWrapper";
import { Loading } from "../../components/Loading";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { findEvents } from "../../database/queries/queries";
import { deleteEventById } from "../../database/queries/commands";
import { ReunionEvent } from "../../utils/types";

export const EventsApp = () => {
  const [isCreating, setIsCreating] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<ReunionEvent | null>(null);
  const queryClient = useQueryClient();

  const { data: events, isLoading, error } = useQuery({
    queryKey: ["events"],
    queryFn: findEvents,
  });

  const { mutateAsync: deleteEvent, isPending: isDeleting } = useMutation({
    mutationFn: deleteEventById,
    onSuccess: async () => {
      setEventToDelete(null);
      await queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });

  if (isCreating) {
    return (
      <ContentWrapper hasScroll>
        <Box sx={{ p: 4, pb: 8 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h4" fontWeight={700}>
              Novo evento
            </Typography>
            <Button variant="outlined" onClick={() => setIsCreating(false)}>
              Voltar para listagem
            </Button>
          </Box>
          <AddEvent embedded onSuccess={() => setIsCreating(false)} />
        </Box>
      </ContentWrapper>
    );
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <ContentWrapper hasScroll>
      <Box sx={{ p: 4, pb: 8 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Gerenciar eventos
            </Typography>
            <Typography color="text.secondary">
              Visualize e remova eventos cadastrados.
            </Typography>
          </Box>
          <Button variant="contained" onClick={() => setIsCreating(true)}>
            Novo evento
          </Button>
        </Box>

        {error && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Nao foi possivel carregar eventos do Firebase.
          </Alert>
        )}

        <List
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {events && events.length > 0 ? (
            events.map((event: ReunionEvent) => (
              <ListItem key={event.id} sx={{ padding: 0 }}>
                <Card sx={{ width: "100%", borderRadius: "16px" }}>
                  {event.imageUrl && (
                    <CardMedia
                      component="img"
                      height="180"
                      image={event.imageUrl}
                      alt={event.title}
                      sx={{ objectFit: "cover" }}
                    />
                  )}
                  <CardContent>
                    <Typography variant="h6" fontWeight={700}>
                      {event.title}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 1 }}>
                      {event.date} {event.time ? `- ${event.time}` : ""}
                    </Typography>
                    <Typography sx={{ mb: 1 }}>{event.location}</Typography>
                    <Typography sx={{ mb: 2 }}>{event.description}</Typography>
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        color="error"
                        variant="outlined"
                        onClick={() => setEventToDelete(event)}
                      >
                        Excluir evento
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </ListItem>
            ))
          ) : (
            <Box textAlign="center" mt={4}>
              <CompassCalibration sx={{ fontSize: 64 }} color="disabled" />
              <Typography variant="h5" color="textSecondary">
                Sem eventos cadastrados
              </Typography>
            </Box>
          )}
        </List>
      </Box>

      <Dialog
        open={Boolean(eventToDelete)}
        onClose={() => setEventToDelete(null)}
        aria-labelledby="delete-event-dialog-title"
        aria-describedby="delete-event-dialog-description"
      >
        <DialogTitle id="delete-event-dialog-title">Excluir evento</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-event-dialog-description">
            Tem certeza que deseja excluir o evento "{eventToDelete?.title}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventToDelete(null)}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            disabled={isDeleting}
            onClick={async () => {
              if (eventToDelete?.id) {
                await deleteEvent(eventToDelete.id);
              }
            }}
          >
            {isDeleting ? "Excluindo..." : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>
    </ContentWrapper>
  );
};
